/**
 * Iter XX.78 — Scorecard flow: offer → upload → OCR → confirm →
 * follow-up (celebrate a pattern OR invite one/two more).
 *
 * Deliberately self-contained. Owns the upload input, the confirm
 * sheet, and the follow-up card. Chat.jsx just renders one instance
 * after the first R3-seeded assistant reply and passes through:
 *   · sessionId    — required for /scorecards/upload + /confirm
 *   · playerName   — optional OCR hint
 *   · onFollowup   — called with the rendered follow-up body so Chat
 *                    can inject it as a new assistant message
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { track as clarityTrack } from "../lib/clarity";

const BASE = "/scorecards";

function Chip({ children, onClick, variant = "primary", disabled, testId }) {
  const base =
    "inline-flex items-center justify-center rounded-full border " +
    "text-[13px] px-4 py-1.5 transition-colors";
  const styles = {
    primary:
      "border-emerald-700 bg-emerald-950/40 text-emerald-200 " +
      "hover:border-emerald-500",
    ghost:
      "border-neutral-800 text-neutral-400 hover:border-neutral-600 " +
      "hover:text-neutral-100",
    danger:
      "border-rose-800 text-rose-200 hover:border-rose-500",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles[variant]} disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function OfferCard({ copy, onUpload, onSkip, submitting }) {
  return (
    <div
      data-testid="scorecard-offer-card"
      className="rounded-xl border border-emerald-900/50 bg-emerald-950/20 p-4 space-y-3"
    >
      <div className="text-[14px] leading-relaxed text-emerald-100 whitespace-pre-line">
        {copy?.body}
      </div>
      <div className="text-[11px] text-emerald-300/70">{copy?.upload_hint}</div>
      <div className="flex flex-wrap gap-2">
        <Chip onClick={onUpload} disabled={submitting}
              testId="scorecard-offer-upload">
          {copy?.cta_upload}
        </Chip>
        <Chip variant="ghost" onClick={onSkip} disabled={submitting}
              testId="scorecard-offer-skip">
          {copy?.cta_skip}
        </Chip>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, min = 0, max = 999, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 text-[13px]">
      <span className="text-neutral-400">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        data-testid={testId}
        className="w-20 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-neutral-100 text-right focus:outline-none focus:border-emerald-700"
      />
    </label>
  );
}

function ConfirmSheet({ copy, ocr, onConfirm, onRetry, submitting }) {
  // Preload the sheet with whatever OCR gave us — the player only
  // fixes what's wrong, they don't fill from scratch.
  const initBatting = (ocr?.batting_present && ocr?.batting) || {};
  const initBowling = (ocr?.bowling_present && ocr?.bowling) || {};
  const [role, setRole] = useState(
    ocr?.batting_present && !ocr?.bowling_present ? "batting" :
    ocr?.bowling_present && !ocr?.batting_present ? "bowling" :
    (initBatting.runs != null ? "batting" : "bowling")
  );
  const [batting, setBatting] = useState({
    runs: initBatting.runs ?? null,
    balls: initBatting.balls ?? null,
    position: initBatting.position ?? null,
    fours: initBatting.fours ?? null,
    sixes: initBatting.sixes ?? null,
    dismissal_text: initBatting.dismissal_text ?? "",
  });
  const [bowling, setBowling] = useState({
    overs: initBowling.overs ?? null,
    maidens: initBowling.maidens ?? null,
    runs: initBowling.runs ?? null,
    wickets: initBowling.wickets ?? null,
  });

  function setB(key, val) { setBatting((s) => ({ ...s, [key]: val })); }
  function setBw(key, val) { setBowling((s) => ({ ...s, [key]: val })); }

  return (
    <div
      data-testid="scorecard-confirm-sheet"
      className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 space-y-4"
    >
      <div className="space-y-1">
        <div className="text-[15px] text-neutral-100 font-medium">{copy?.title}</div>
        <div className="text-[12px] text-neutral-500">{copy?.subtitle}</div>
      </div>

      <div className="flex gap-2 text-[12px]">
        {["batting", "bowling", "keeping"].map((r) => (
          <button
            key={r}
            data-testid={`scorecard-confirm-role-${r}`}
            onClick={() => setRole(r)}
            className={
              "px-3 py-1 rounded-full border transition-colors " +
              (role === r
                ? "border-emerald-600 text-emerald-200 bg-emerald-950/40"
                : "border-neutral-800 text-neutral-500 hover:border-neutral-600")
            }
          >
            {r}
          </button>
        ))}
      </div>

      {role === "batting" && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <NumberField label="Runs"     value={batting.runs}
                       onChange={(v) => setB("runs", v)} testId="conf-runs" />
          <NumberField label="Balls"    value={batting.balls}
                       onChange={(v) => setB("balls", v)} testId="conf-balls" />
          <NumberField label="Position" value={batting.position} max={11}
                       onChange={(v) => setB("position", v)} testId="conf-pos" />
          <NumberField label="4s"       value={batting.fours}
                       onChange={(v) => setB("fours", v)} testId="conf-fours" />
          <NumberField label="6s"       value={batting.sixes}
                       onChange={(v) => setB("sixes", v)} testId="conf-sixes" />
          <label className="col-span-2 text-[13px] flex items-center gap-3">
            <span className="text-neutral-400 shrink-0">How out</span>
            <input
              type="text"
              value={batting.dismissal_text || ""}
              onChange={(e) => setB("dismissal_text", e.target.value)}
              placeholder="e.g. c Sharma b Kumar"
              data-testid="conf-dismissal"
              className="flex-1 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-neutral-100 focus:outline-none focus:border-emerald-700"
            />
          </label>
        </div>
      )}

      {role === "bowling" && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <NumberField label="Overs"   value={bowling.overs}
                       onChange={(v) => setBw("overs", v)} testId="conf-overs" />
          <NumberField label="Maidens" value={bowling.maidens} max={60}
                       onChange={(v) => setBw("maidens", v)} testId="conf-mdns" />
          <NumberField label="Runs"    value={bowling.runs}
                       onChange={(v) => setBw("runs", v)} testId="conf-brs" />
          <NumberField label="Wickets" value={bowling.wickets} max={10}
                       onChange={(v) => setBw("wickets", v)} testId="conf-wkts" />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Chip
          onClick={() => onConfirm({ role, batting, bowling })}
          disabled={submitting}
          testId="scorecard-confirm-ok"
        >
          {copy?.cta_ok}
        </Chip>
        <Chip variant="ghost" onClick={onRetry} disabled={submitting}
              testId="scorecard-confirm-retry">
          {copy?.cta_redo}
        </Chip>
      </div>
    </div>
  );
}

function FollowupCard({ followup, onUpload, onDismiss }) {
  if (!followup) return null;
  const isInvite = followup.kind === "invite_more";
  return (
    <div
      data-testid={`scorecard-followup-${followup.kind}`}
      className={
        "rounded-xl border p-4 space-y-3 " +
        (isInvite
          ? "border-emerald-900/50 bg-emerald-950/20"
          : "border-amber-900/50 bg-amber-950/20")
      }
    >
      <div className={
        "text-[14px] leading-relaxed whitespace-pre-line " +
        (isInvite ? "text-emerald-100" : "text-amber-100")
      }>
        {followup.body}
      </div>
      {isInvite && (
        <div className="flex flex-wrap gap-2">
          <Chip onClick={onUpload} testId="scorecard-followup-upload">
            Upload another scorecard
          </Chip>
          <Chip variant="ghost" onClick={onDismiss}
                testId="scorecard-followup-skip">
            Maybe later
          </Chip>
        </div>
      )}
    </div>
  );
}

export default function ScorecardFlow({ sessionId, playerName, onFollowup }) {
  const [copy, setCopy] = useState(null);
  const [phase, setPhase] = useState("offer"); // offer|uploading|confirm|done|dismissed
  const [ocr, setOcr] = useState(null);
  const [scorecardId, setScorecardId] = useState(null);
  const [followup, setFollowup] = useState(null);
  const fileInputRef = useRef(null);
  const sentRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(`${BASE}/config`).then((r) => {
      if (alive) setCopy(r.data);
    }).catch(() => { /* copy is best-effort; fall back to inline defaults */ });
    return () => { alive = false; };
  }, []);

  function pickFile() {
    // Iter XX.79 · Clarity funnel — the moment the file picker opens
    // is the actual "upload started" intent signal. Server-side we
    // only see the completed upload.
    clarityTrack("scorecard_upload_started", { session_id: sessionId });
    if (fileInputRef.current) fileInputRef.current.click();
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-select of same file after Retry
    if (!file) return;
    // Client-side sanity — server enforces too but skip the round-trip.
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB).");
      return;
    }
    setPhase("uploading");
    const form = new FormData();
    form.append("session_id", sessionId);
    if (playerName) form.append("player_name", playerName);
    form.append("file", file, file.name);
    try {
      const { data } = await api.post(`${BASE}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      setScorecardId(data.scorecard_id);
      setOcr(data.ocr);
      // Iter XX.79 · Clarity — upload round-trip succeeded (OCR may
      // still have failed; we track that separately via `ocr_status`).
      clarityTrack("scorecard_uploaded", {
        session_id: sessionId,
        scorecard_id: data.scorecard_id,
        ocr_status: data.ocr?.status,
        ocr_confidence: data.ocr?.ocr_confidence,
      });
      // Move to confirm ONLY if OCR extracted something usable.
      // If it failed, the confirm sheet still opens with blank fields —
      // that's the coaching-conversation fallback the founder asked for.
      setPhase("confirm");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
      setPhase("offer");
    }
  }

  async function confirm(fields) {
    if (sentRef.current) return;
    sentRef.current = true;
    try {
      const { data } = await api.post(`${BASE}/${scorecardId}/confirm`, {
        session_id: sessionId,
        role: fields.role,
        batting: fields.role === "batting" ? fields.batting : null,
        bowling: fields.role === "bowling" ? fields.bowling : null,
      });
      setFollowup(data.followup || null);
      setPhase("done");
      // Iter XX.79 · Clarity — confirmed = the real signal the
      // scorecard produced usable data for coaching.
      clarityTrack("scorecard_confirmed", {
        session_id: sessionId,
        scorecard_id: scorecardId,
        role: fields.role,
        pattern_action: data?.pattern?.action,
        n_confirmed: data?.pattern?.n_confirmed,
      });
      if (onFollowup) onFollowup(data);
    } catch (err) {
      sentRef.current = false;
      toast.error(err?.response?.data?.detail || "Couldn't save. Try again.");
    }
  }

  function retry() {
    setPhase("offer");
    setOcr(null);
    setScorecardId(null);
    sentRef.current = false;
  }

  function dismiss() {
    setPhase("dismissed");
  }

  if (!copy) return null;
  if (phase === "dismissed") return null;

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid="scorecard-file-input"
      />

      {phase === "offer" && (
        <OfferCard
          copy={copy.offer}
          onUpload={pickFile}
          onSkip={dismiss}
          submitting={false}
        />
      )}

      {phase === "uploading" && (
        <div
          data-testid="scorecard-uploading"
          className="rounded-xl border border-neutral-800 bg-neutral-950/40 p-4 text-[13px] text-neutral-400"
        >
          Reading your scorecard…
        </div>
      )}

      {phase === "confirm" && (
        <ConfirmSheet
          copy={copy.confirm}
          ocr={ocr}
          onConfirm={confirm}
          onRetry={retry}
          submitting={false}
        />
      )}

      {phase === "done" && (
        <FollowupCard
          followup={followup}
          onUpload={() => { setPhase("offer"); sentRef.current = false; }}
          onDismiss={() => setPhase("dismissed")}
        />
      )}
    </div>
  );
}
